<template>
  <v-container class="mb-16">
    <loading-overlay v-model="loading"/>

    <v-toolbar flat>
      <v-spacer />
      <v-toolbar-title class="text-h4 font-weight-bold">
        오렌즈 실무경험 지원서
      </v-toolbar-title>
      <v-spacer />
    </v-toolbar>

    <v-form ref="apply-form-temp"
            v-model="validTemp"
            lazy-validation
            autocomplete="off"
    >
      <v-input type="hidden" :value="formData.name"
               hide-details
               :rules="[v=> !!v || '이름을 입력해 주세요.']"
      />
      <v-input type="hidden" :value="formData.mobile_number"
               hide-details
               :rules="[
                 v=> !!v || '휴대폰 번호를 입력해 주세요.',
                 v => !v || !!v.match(mobileNoRegExp) || '휴대폰 번호를 형식에 맞게 입력해 주세요.',
               ]"
      />
      <v-input type="hidden" :value="formData.pw"
               hide-details
               :rules="[
                 v=> !!v || '비밀 번호를 입력해 주세요.',
                 v => !v || !!v.match(onlyNumberRegExp) || '숫자 를 입력해 주세요',
                 v => !v || v.length === 4 || '4자리를 입력해 주세요.',
               ]"
      />
    </v-form>

    <v-form ref="apply-form"
            v-model="valid"
            lazy-validation
            autocomplete="off"
    >
      <v-row>
        <v-col cols="12">
          <v-row>
            <v-spacer />
            <v-col class="text-center">
              <v-icon>$complete</v-icon>
              표시는 필수 입력 항목입니다.
            </v-col>
            <v-spacer />
          </v-row>
        </v-col>
        <v-col cols="12" md="2" xl="2"
               class="mt-2 mb-8"
        >
          <v-row>
            <v-spacer />
            <v-col>
              <v-input type="hidden" :value="formData.imagePath"
                       hide-details
                       :rules="[v => !!v || '이미지를 첨부해 주세요.']"
              />

              <v-sheet class="image-sheet">
                <v-img width="113" height="151"
                       :aspect-ratio="113/151"
                       contain
                       class="mx-auto"
                       aria-controls
                       :src="formData.imagePath"
                       @click="openCropperDialog()"
                >
                  <div v-if="!formData.imagePath" style="width: 113px; height: 151px; border: 1px rgb(128,128,128) solid"
                       class="d-flex justify-center align-center text-center"
                  >
                    이미지 첨부<br>
                    (3x4)
                  </div>
                </v-img>

                <v-btn v-if="formData.imagePath"
                       color="secondary"
                       class="reset-image"
                       fab tile x-small
                       depressed
                       title="이미지 삭제"
                       @click="resetImage"
                >
                  <v-icon>$close</v-icon>
                </v-btn>
                <v-btn v-else
                       color="secondary"
                       class="reset-image"
                       fab tile x-small
                       depressed
                       title="필수 입력"
                >
                  <v-icon>$complete</v-icon>
                </v-btn>
              </v-sheet>
            </v-col>
            <v-spacer />
          </v-row>
        </v-col>

        <v-col cols="12" md="10" xl="10">
          <v-row>
            <v-col v-for="(item, index) in applyFormSectionItems.personalInformation.inputs"
                   :key="`personalInformation-${index}`"
                   v-bind="item.colProps"
            >
              <component :is="item.inputInfos.inputComponent"
                         v-if="!item.inputInfos.radioOptions"
                         v-bind="{...item.inputInfos.inputProps, ...passwordConfirm(item.inputName)}"
                         v-model="formData[item.inputName]"
              />
              <template v-if="item.inputInfos.radioOptions">
                <v-radio-group class="mt-0" v-model="formData[item.inputName]" row>
                  <v-sheet class="font-weight-bold pr-10">{{item.inputInfos.inputProps.label}}</v-sheet>
                  <v-radio v-for="(radio, idx) in item.inputInfos.radioOptions" v-bind:key="idx" :label="radio.key" :value="radio.value"/>
                </v-radio-group>
                <template v-if="item.inputName === 'military'">
                  <v-checkbox v-model="isMilitaryAgree" class="d-inline-block mt-0" label="(필수) 민감정보 수집에 동의합니다." :rules="[v => !!v || '민감정보에 동의해주세요.']"/>
                  <v-btn class="mb-3 ml-3" icon @click="toggleMilitaryAgree">
                    <v-icon large v-show="showMilitaryAgree">
                      $menuUp
                    </v-icon>
                    <v-icon large v-show="!showMilitaryAgree">
                      $menuDown
                    </v-icon>
                  </v-btn>
                  <ol class="military_agree pa-5 pl-10 mb-3" v-show="showMilitaryAgree">
                    <li>수집.이용목적 : 개인 의사에 따른 구직활동</li>
                    <li>수집 항목 : 병역사항</li>
                    <li>보유 및 이용기산 : 서비스 이용 시점까지</li>
                    <li>민감정보는 선택 항목이며, 입력하지 않아도 이력서 작성에 제한을 두지 않습니다.</li>
                  </ol>
                </template>
              </template>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row v-for="(item, index) in applyFormSections"
             :key="`applyFormSections-${index}`"
             class="mb-8"
      >
        <v-col v-if="applyFormSectionItems[item] && applyFormSectionItems[item].title"
               cols="12"
        >
          <v-toolbar v-bind="toolbarProps">
            <v-toolbar-title>{{ applyFormSectionItems[item] && applyFormSectionItems[item].title }}</v-toolbar-title>
          </v-toolbar>
        </v-col>

        <v-col v-if="applyFormSectionItems[item] && applyFormSectionItems[item].description"
               cols="12"
               class="py-0"
        >
          <v-alert v-bind="alertProps">
            <div v-html="applyFormSectionItems[item] && applyFormSectionItems[item].description"/>
          </v-alert>
        </v-col>

        <v-col v-for="(sectionInputs, subIndex) in applyFormSectionItems[item].inputs"
               v-show="!$vuetify.breakpoint.smAndDown || sectionInputs.inputInfos.inputComponent !== 'v-spacer'"
               :key="`applyFormSectionItems-${subIndex}`"
               v-bind="sectionInputs.colProps"
        >
          <component :is="sectionInputs.inputInfos.inputComponent"
                     v-bind="sectionInputs.inputInfos.inputProps"
                     v-model="formData[sectionInputs.inputName]"
          />
        </v-col>
      </v-row>


      <v-toolbar flat class="mt-16 mb-2">
        <v-spacer/>
        <v-toolbar-title class="text-h4 font-weight-bold">
          오렌즈 아카데미 자기소개서
        </v-toolbar-title>
        <v-spacer/>
      </v-toolbar>

      <v-row v-for="(item, index) in applyFormIntroduceSections"
             :key="`applyFormIntroduceSections-${index}`"
             class="mb-8"
      >
        <v-col v-if="applyFormSectionItems[item] && applyFormSectionItems[item].title"
               cols="12"
        >
          <v-toolbar v-bind="toolbarProps"
                     color="secondary"
          >
            <v-toolbar-title>{{ applyFormSectionItems[item] && applyFormSectionItems[item].title }}</v-toolbar-title>
          </v-toolbar>
        </v-col>

        <v-col v-if="applyFormSectionItems[item] && applyFormSectionItems[item].description"
               cols="12"
               class="py-0"
        >
          <v-alert v-bind="alertProps">
            <div v-html="applyFormSectionItems[item] && applyFormSectionItems[item].description"/>
          </v-alert>
        </v-col>

        <v-col v-for="(sectionInputs, subIndex) in applyFormSectionItems[item].inputs"
               v-show="!$vuetify.breakpoint.smAndDown || sectionInputs.inputInfos.inputComponent !== 'v-spacer'"
               :key="`applyFormSectionItems-${subIndex}`"
               v-bind="sectionInputs.colProps"
        >
          <component :is="sectionInputs.inputInfos.inputComponent"
                     v-bind="sectionInputs.inputInfos.inputProps"
                     v-model="formData[sectionInputs.inputName]"
          />
        </v-col>
      </v-row>


      <v-app-bar :height="$vuetify.breakpoint.smAndDown ? 140 : 64"
                 fixed bottom
      >
        <v-row class="apply">
          <v-col cols="5" class="hidden-sm-and-down">
            <v-sheet class="text-center transparent">
              지원하기 제출 시 수정이 불가능 합니다.<br/>
              (관련 문의: 02-6285-5162  연락 바랍니다.)
            </v-sheet>
          </v-col>
          <v-col cols="12" class="hidden-md-and-up">
            <v-sheet class="text-center transparent">
              제출 시 수정 불가<br/>
              문의:02-6285-5162
            </v-sheet>
          </v-col>
          <v-col cols="5"
                 class="apply__btnWrap hidden-sm-and-down"
          >
            <v-btn color="primary"
                   depressed
                   large
                   outlined
                   @click="temporarySave"
            >
              <span>임시저장</span>
            </v-btn>
            <v-btn color="secondary"
                   depressed
                   large
                   @click="saveLoad('임시저장 불러오기','N')"
            >
              <span>불러오기</span>
            </v-btn>
            <v-btn color="primary"
                   depressed
                   large
                   @click="store"
            >
              <span>지원하기</span>
            </v-btn>
            <v-btn color="#ff5252"
                   outlined
                   depressed
                   large
                   @click="saveLoad('내 지원서 확인','Y')"
            >
              <span>내 지원서 확인</span>
            </v-btn>
          </v-col>
          <v-col cols="12"
                 class="apply__btnWrap hidden-md-and-up"
          >
            <v-btn color="primary"
                   depressed
                   small
                   outlined
                   @click="temporarySave"
            >
              <span>임시저장</span>
            </v-btn>
            <v-btn color="secondary"
                   depressed
                   small
                   @click="saveLoad('임시저장 불러오기','N')"
            >
              <span>불러오기</span>
            </v-btn>
            <v-btn color="primary"
                   depressed
                   small
                   @click="store"
            >
              <span>지원하기</span>
            </v-btn>
            <v-btn color="#ff5252"
                   outlined
                   depressed
                   small
                   @click="saveLoad('내 지원서 확인','Y')"
            >
              <span>내 지원서 확인</span>
            </v-btn>
          </v-col>
        </v-row>
      </v-app-bar>
    </v-form>

    <cropper-dialog v-if="showCropperDialog"
                    v-model="showCropperDialog"
                    dialog-title="지원서 이미지"
                    :dialog-width="960"
                    :image-path="formData.imagePath"
                    :cropper-options="cropperOptions"
                    :preview-options="previewOptions"
                    @cropped-image="croppedImage($event)"
    />
    <privacy-dialog v-if="showPrivacyDialog"
                    v-model="showPrivacyDialog"
    />
    <temporary-load-dialog v-if="showTemporaryLoadDialog"
                           v-model="showTemporaryLoadDialog"
                           :dialog-title="dialogTitle"
                           :apply-yn="applyYn"
                           :dialog-width="320"
                           @apply-form-data="applyFormData"
    />
    <applicant-view-dialog v-if="showApplicantDialog"
                           v-model="showApplicantDialog"
                           :applicant="applicant"
    />
  </v-container>
</template>

<script>

import {VTextField, VTextarea, VSpacer, VSelect} from 'vuetify/lib';
import ApplicantViewDialog from '@/components/common/dialog/ApplicantViewDialog';
import LoadingOverlay from '@/components/common/LoadingOverlay';
import CropperDialog from '@/components/common/dialog/CropperDialog';
import TemporaryLoadDialog from "@/components/common/dialog/TemporaryLoadDialog.vue";
import {formValidateMixin} from '@/mixins/formValidateMixin';
import {errorControlMixin} from '@/mixins/errorControlMixin';
import {alertProps, toolbarProps, inputTextProps} from '@/assets/props/defaultProps';
import {applyFormInfos} from '@/assets/infos/applyFormInfos';
import PrivacyDialog from '@/components/common/dialog/PrivacyDialog';
import {mobileNoRegExp,onlyNumberRegExp} from '@/assets/commonRules';
export default {
  name: 'ApplyForm',
  components: {
    TemporaryLoadDialog,
    PrivacyDialog,
    LoadingOverlay,
    VTextField, VTextarea, VSpacer, VSelect,
    CropperDialog,
    ApplicantViewDialog,
  },
  mixins: [formValidateMixin, errorControlMixin],
  data: () => ({
    alertProps, toolbarProps, inputTextProps,mobileNoRegExp,onlyNumberRegExp,
    loading: false,
    showPrivacyDialog: true,
    showCropperDialog: false,
    showTemporaryLoadDialog: false,
    showApplicantDialog: false,
    isMilitaryAgree: false,
    showMilitaryAgree: false,
    cropperOptions: {
      width: 113,
      aspectRatio: 113 / 151,
    },
    previewOptions: {
      width: 113,
      height: 151,
    },
    formData: {
      privacy_agree: 'Y',
      imagePath: null,
      participation_round: '1차',
      internship: '미신청',
      military: null,
    },
    dialogTitle: '',
    applyYn:null,
    applicant:null,
  }),
  computed: {
    applyFormSections: function () {
      return applyFormInfos.sections.filter(
        section => ['personalInformation'].some(checkSection => section !== checkSection),
      );
    },
    applyFormIntroduceSections: function () {
      return applyFormInfos.introduceSections;
    },
    applyFormSectionItems: function () {
      return applyFormInfos.sectionItems;
    },
  },
  mounted() {},
  methods: {
    openCropperDialog() {
      this.showCropperDialog = true;
    },
    toggleMilitaryAgree () {
      this.showMilitaryAgree = !this.showMilitaryAgree;
    },
    croppedImage(image) {
      this.formData.imagePath = image;
      this.showCropperDialog = false;
    },
    resetImage() {
      this.formData.imagePath = null;
    },
    // 지원완료
    store() {
      // validation && focus
      if (!this.formValidate('apply-form')) {
        return;
      }

      this.loading = true;

      this.$axios.post('/apply', this.formData).then(() => {
        this.$dialog.message.success('지원이 완료되었습니다. 좋은 결과가 있길 응원하겠습니다.', {position: 'top', timeout: 1500}).then(() => {
          window.location.href = 'http://www.starvision8800.com/default/m5/s4.php?m=5&sm=53';
        });
        return;
      }).catch((error) => {
        this.errorControl(error);

      }).finally(() => {
        this.loading = false;

      });
    },
    // 임시저장
    temporarySave() {
      if (!this.formValidate('apply-form-temp')) {
        return;
      }
      this.loading = true;
      this.$axios.put('/apply', this.formData).then(() => {
      }).catch((error) => {
        this.errorControl(error);

      }).finally(() => {
        this.loading = false;
      });
    },
    // 임시저장 불러오기, 내 지원서 확인
    saveLoad(title, applyYn) {
      this.dialogTitle = title;
      this.applyYn = applyYn;
      this.showTemporaryLoadDialog = true;
    },
    applyFormData({ data, applyYn }) {
      if (applyYn === "Y") {
        this.applicant = data;
        this.showApplicantDialog = true;
      } else {
        this.formData = data;
      }
    },
    passwordConfirm(inputName) {
      if (inputName === 'password_confirm') {
        return {
          rules: [
            v => v && v === this.formData.pw || '비밀번호가 일치하지 않습니다.',
          ],
        };
      }
      return;
    },
  },
};
</script>
<style lang="scss">
.apply {
  justify-content: center;

  &__btnWrap {
    display: flex;
    justify-content: center;
    gap: 5px;
  }
}
.military_agree {color:#666; background:#eee; border-radius:10px;}
</style>